import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import { APP_VERSION, BUILD_DATE } from '../config';
import { 
  Music, Image, Gift, Video, LayoutDashboard, ChevronDown, 
  Zap, LogOut, User, Settings, Menu, X, Info, Sparkles
} from 'lucide-react';

const BANNER_PLAIN = "/assets/brand/logo-inline.png";

export default function MainNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const navItems = [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, disabled: false },
    { label: 'Create Song', path: '/create-song', icon: Music, disabled: false },
    { label: 'Album Cover', path: '/create-cover', icon: Image, disabled: false },
    { label: 'Music Gift', path: '/create-gift', icon: Gift, disabled: false },
    { label: 'Video', path: '/create-video', icon: Video, disabled: false },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav">
      <div className="container-wide flex items-center justify-between h-16">
        {/* Logo */}
        <div 
          className="flex items-center cursor-pointer" 
          onClick={() => navigate('/dashboard')}
        >
          <img src={BANNER_PLAIN} alt="Wrapped In Music" className="h-8 w-auto" />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => !item.disabled && navigate(item.path)}
              disabled={item.disabled}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${isActive(item.path) 
                  ? 'bg-primary/20 text-primary' 
                  : item.disabled 
                    ? 'text-zinc-600 cursor-not-allowed' 
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {item.disabled && <span className="text-xs text-zinc-600">(Soon)</span>}
            </button>
          ))}
        </div>

        {/* Right Side - Credits & User */}
        <div className="flex items-center gap-4">
          {/* Credits Badge - Clickable to buy credits */}
          <button 
            onClick={() => navigate('/dashboard?buyCredits=true')}
            className="credit-badge hidden sm:flex cursor-pointer hover:scale-105 transition-transform" 
            data-testid="nav-credits"
            title="Click to buy credits"
          >
            <Zap className="h-4 w-4" />
            {user?.credits || 0}
          </button>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden p-2 text-zinc-400 hover:text-white"
          >
            {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* User Menu */}
          <div className="relative hidden md:block">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors"
              data-testid="user-menu-btn"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
              )}
              <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 glass-dropdown py-2 shadow-xl">
                <div className="px-4 py-2 border-b border-white/10">
                  <p className="text-sm text-white truncate">{user?.name}</p>
                  <p className="text-xs text-zinc-400 truncate">{user?.email}</p>
                </div>
                {user?.role === 'admin' && (
                  <button 
                    onClick={() => { navigate('/admin'); setShowUserMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-zinc-400 hover:bg-white/5 flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Admin Panel
                  </button>
                )}
                <button 
                  onClick={() => { navigate('/my-ai-clips'); setShowUserMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-zinc-400 hover:bg-white/5 flex items-center gap-2"
                  data-testid="nav-my-ai-clips"
                >
                  <Sparkles className="h-4 w-4" />
                  My AI Clips
                </button>
                <button 
                  onClick={() => { navigate('/settings'); setShowUserMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-zinc-400 hover:bg-white/5 flex items-center gap-2"
                >
                  <User className="h-4 w-4" />
                  Account Settings
                </button>
                <button 
                  onClick={logout}
                  className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-white/5 flex items-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
                {/* Version info */}
                <div className="px-4 py-2 border-t border-white/10 mt-1">
                  <p className="text-[10px] text-zinc-500 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    {APP_VERSION} • {BUILD_DATE}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="md:hidden glass-card mx-4 mb-4 py-2">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                if (!item.disabled) {
                  navigate(item.path);
                  setShowMobileMenu(false);
                }
              }}
              disabled={item.disabled}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium
                ${isActive(item.path) 
                  ? 'bg-primary/20 text-primary' 
                  : item.disabled 
                    ? 'text-zinc-600' 
                    : 'text-zinc-400 hover:bg-white/5'
                }`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
              {item.disabled && <span className="text-xs text-zinc-600 ml-auto">(Coming Soon)</span>}
            </button>
          ))}
          <div className="border-t border-white/10 mt-2 pt-2">
            {user?.role === 'admin' && (
              <button 
                onClick={() => { navigate('/admin'); setShowMobileMenu(false); }}
                className="w-full px-4 py-3 text-left text-sm text-zinc-400 hover:bg-white/5 flex items-center gap-3"
              >
                <Settings className="h-5 w-5" />
                Admin Panel
              </button>
            )}
            <button 
              onClick={() => { navigate('/my-ai-clips'); setShowMobileMenu(false); }}
              className="w-full px-4 py-3 text-left text-sm text-zinc-400 hover:bg-white/5 flex items-center gap-3"
            >
              <Sparkles className="h-5 w-5" />
              My AI Clips
            </button>
            <button 
              onClick={() => { navigate('/settings'); setShowMobileMenu(false); }}
              className="w-full px-4 py-3 text-left text-sm text-zinc-400 hover:bg-white/5 flex items-center gap-3"
            >
              <User className="h-5 w-5" />
              Account Settings
            </button>
            <button 
              onClick={logout}
              className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-white/5 flex items-center gap-3"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
